import { useEffect, useState } from 'react';
import { Marker, Polyline, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import { resolveLineStyle, dashArray, visibleLabel, hoseSummary } from '../lib/lineStyles';
import { pathLength } from '../lib/geo';

// Linie, Pfeil oder Schlauchleitung (type 'polyline') mit Beschriftung und ggf. Pfeilspitze

const toLatLng = (p) => [p.lat, p.lng];

const useZoom = () => {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  useEffect(() => {
    const update = () => setZoom(map.getZoom());
    map.on('zoomend', update);
    return () => map.off('zoomend', update);
  }, [map]);
  return zoom;
};

// Pfeilspitze, bei 0° nach rechts; die Spitze liegt in der Icon-Mitte und bleibt beim Drehen auf dem Endpunkt
const headSize = (weight) => ({ length: 10 + weight * 2.2, half: 5 + weight * 1.4 });

const headIcon = (color, weight, head, angle, isSelected) => {
  const { length, half } = headSize(weight);
  const size = Math.ceil(length * 2 + 8);
  const c = size / 2;
  const shape = head === 'open'
    ? `<path d="M${c - length} ${c - half} L${c} ${c} L${c - length} ${c + half}" fill="none" stroke="${color}" stroke-width="${Math.max(2.5, weight * 0.8)}" stroke-linecap="round" stroke-linejoin="round"/>`
    : `<path d="M${c - length} ${c - half} L${c} ${c} L${c - length} ${c + half} Z" fill="${color}" stroke="white" stroke-width="1.5" stroke-linejoin="round"/>`;
  return L.divIcon({
    html: `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" class="${isSelected ? 'selected-element' : ''}" style="transform: rotate(${angle}deg); overflow: visible">${shape}</svg>`,
    className: 'line-arrow-head',
    iconSize: [size, size],
    iconAnchor: [c, c],
  });
};

export const LineElement = ({ element, isSelected, onClick }) => {
  const map = useMap();
  const zoom = useZoom();
  const { positions = [], properties = {} } = element;
  const style = resolveLineStyle(properties);

  if (positions.length < 2) return null;

  const latLngs = positions.map(toLatLng);
  let linePositions = latLngs;
  let headAngle = 0;

  if (style.head) {
    // Richtung des letzten Abschnitts in Bildschirmkoordinaten (Web Mercator ist winkeltreu)
    const tip = map.project(latLngs[latLngs.length - 1], zoom);
    const before = map.project(latLngs[latLngs.length - 2], zoom);
    const dx = tip.x - before.x;
    const dy = tip.y - before.y;
    const distance = Math.hypot(dx, dy) || 1;
    headAngle = (Math.atan2(dy, dx) * 180) / Math.PI;
    // Gefüllte Spitze: Linie endet unter der Spitze, damit sie nicht seitlich herausragt
    if (style.head === 'filled') {
      const back = Math.min(headSize(style.weight).length * 0.7, distance * 0.9);
      const end = map.unproject(L.point(tip.x - (dx / distance) * back, tip.y - (dy / distance) * back), zoom);
      linePositions = [...latLngs.slice(0, -1), [end.lat, end.lng]];
    }
  }

  const label = properties.kind === 'hose'
    ? [visibleLabel(properties.label), hoseSummary(properties, pathLength(positions))].filter(Boolean).join(' – ')
    : visibleLabel(properties.label);

  const handlers = { click: () => onClick?.(element) };

  return (
    <>
      {/* Breite, unsichtbare Klick-/Tippfläche, damit auch dünne Linien leicht auszuwählen sind */}
      <Polyline
        positions={latLngs}
        pathOptions={{ color: style.color, weight: Math.max(18, style.weight + 12), opacity: 0 }}
        // Klick nicht an die Karte weitergeben, die sonst die Auswahl sofort wieder aufhebt
        bubblingMouseEvents={false}
        eventHandlers={handlers}
      />
      <Polyline
        positions={linePositions}
        interactive={false}
        pathOptions={{
          color: style.color,
          weight: isSelected ? style.weight + 3 : style.weight,
          opacity: isSelected ? 1 : 0.9,
          dashArray: dashArray(style.dashed, style.weight),
          lineCap: style.head ? 'butt' : 'round',
        }}
      >
        {label && (
          <Tooltip permanent direction="center" className="line-label">{label}</Tooltip>
        )}
      </Polyline>
      {style.head && (
        <Marker
          position={latLngs[latLngs.length - 1]}
          icon={headIcon(style.color, style.weight, style.head, headAngle, isSelected)}
          interactive={false}
          keyboard={false}
        />
      )}
    </>
  );
};

export default LineElement;
